/**
 * Program Route Resolution Service
 * 
 * Server-side route resolution for login and program selection flows.
 * Used when we need to eagerly fetch menu permissions and resolve a route.
 * 
 * Configuration-driven: backend returns `landingRoute` or `defaultLandingRoute`
 * which is the authoritative landing destination per role, resolved from
 * ProgramRoleDashboardMappings in the database.
 */

import { apiClient } from '@/modules/shared/services/apiClient';
import { resolveDefaultProgramRoute } from '../utils/resolveDefaultProgramRoute';
import type { ApiResponse } from '@/modules/shared/types';
import { programRoutes, buildProgramRouteFromRelative } from '@/modules/shared/constants/routes';

interface MyProgramMenuAccessDto {
  programId: string;
  programName: string;
  userId: string;
  roleId: string;
  roleName: string;
  roleCategory: string;
  accessibleMenuKeys: string[];
  /** Governance type: CONFIGURATION or OPERATIONAL */
  governanceType?: string;
  /** True if this is a configuration role */
  isConfigurationRole?: boolean;
  /** True if this is an operational role */
  isOperationalRole?: boolean;
  /** Default landing route from backend */
  defaultLandingRoute?: string;
  dashboardKey?: string;
  landingRoute?: string;
}

/**
 * Resolves the default program route based on fetched menu permissions.
 * 
 * DASHBOARD-MAPPING-FIRST BEHAVIOR:
 * 1. Fetches the user's menu permissions and governance info from backend
 * 2. Uses backend-resolved `landingRoute` (from ProgramRoleDashboardMappings)
 * 3. Falls back to `defaultLandingRoute` (relative route from DashboardRoutingService)
 * 4. Last resort: resolves from accessible menu keys
 * 
 * @param programId - The program ID to fetch permissions for
 * @returns The resolved route to navigate to
 * @throws Error if programId is invalid
 */
export async function resolveDefaultProgramRouteFromApi(programId: string): Promise<string> {
  if (!programId || programId.trim() === '') {
    throw new Error('resolveDefaultProgramRouteFromApi: programId is required');
  }

  try {
    // Fetch user's menu permissions from backend
    const response = await apiClient.get<ApiResponse<MyProgramMenuAccessDto>>(
      `/programs/${programId}/role-menu-permissions/my-access`
    );

    if (!response.data.success || !response.data.data) {
      console.warn(
        `resolveDefaultProgramRouteFromApi: Failed to fetch menu permissions for program ${programId}`
      );
      return programRoutes.operationalDashboard(programId);
    }

    const menuAccess = response.data.data;
    const accessibleMenuKeys = menuAccess.accessibleMenuKeys || [];

    if (menuAccess.landingRoute && menuAccess.landingRoute.trim().length > 0) {
      return menuAccess.landingRoute;
    }

    // 1. Backend-provided relative landing route is the single source of truth.
    if (menuAccess.defaultLandingRoute && menuAccess.defaultLandingRoute.trim().length > 0) {
      const route = buildProgramRouteFromRelative(programId, menuAccess.defaultLandingRoute);
      console.debug(
        `resolveDefaultProgramRouteFromApi: Using backend defaultLandingRoute for program ${programId}:`,
        { defaultLandingRoute: menuAccess.defaultLandingRoute, resolvedRoute: route, dashboardKey: menuAccess.dashboardKey, roleName: menuAccess.roleName }
      );
      return route;
    }

    // 2. Fallback: resolve from accessible menu keys (legacy path)
    const route = resolveDefaultProgramRoute(accessibleMenuKeys, programId, {
      isConfigurationRole: menuAccess.isConfigurationRole,
      roleName: menuAccess.roleName,
    });
    
    console.debug(
      `resolveDefaultProgramRouteFromApi: Resolved route for program ${programId}:`,
      {
        programName: menuAccess.programName,
        roleName: menuAccess.roleName,
        governanceType: menuAccess.governanceType,
        isConfigurationRole: menuAccess.isConfigurationRole,
        accessibleMenuCount: accessibleMenuKeys.length,
        resolvedRoute: route,
      }
    );

    return route;
  } catch (error) {
    console.error('resolveDefaultProgramRouteFromApi: Failed to fetch and resolve route:', error);
    // Fallback to operational dashboard as last resort when API is unreachable
    return programRoutes.operationalDashboard(programId);
  }
}
